import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { EmailPreviewRequest } from '../models/email/EmailPreviewRequest';
import { Observable, from, BehaviorSubject } from 'rxjs';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { handleErrorFunction } from '../shared/helpers/handleError';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';
import { EmailPreviewResponse } from '../models/email/EmailPreviewResponse';
import { AddReceivingLogRequest } from '../models/logs/AddReceivingLogRequest';
import { LogEntryPhoto } from '../models/logs/photos/LogEntryPhoto.interface';
import { OperationStatus } from '../models/OperationStatus.interface';
import { PhotoService } from './photo.service';
import { convertAzurePhotosToFiles, convertPhotosToFiles } from '../shared/helpers/photos-utils';
import { PhotoMetadata } from '../models/logs/photos/PhotoMetadata';
import { AssetsDataService } from './assets-data.service';
import { ToastText, LoadingText } from '../models/assets-data/UiConfig.interface';

/**
 * EmailService is responsible for managing the process of composing and sending shipment-related emails.
 * These emails are generated when a user submits a new receiving log entry.
 * It provides methods for generating email content based on receiving logs, sending emails to specified recipients,
 * and constructing email body text for various log types and locations.
 */

@Injectable({
  providedIn: 'root',
})
export class EmailService {
  private _entraService: EntraService;
  private _photoService: PhotoService;
  private _httpClient: HttpClient;
  private _toastService: ToastService;

  // BehaviorSubjects for email preview state
  public emailMessageData = new BehaviorSubject<EmailPreviewResponse>(new EmailPreviewResponse());
  public dataLoading = new BehaviorSubject<{ isLoading: boolean; message?: string }>(
    { isLoading: false }
  );
  
  private dataLoadingDefault = { isLoading: false };


  public loadingText: LoadingText;
  public toastText: ToastText;

  constructor(
    entraService: EntraService,
    photoService: PhotoService,
    httpClient: HttpClient,
    toastService: ToastService,
    assetsDataService: AssetsDataService
  ) {
    this._entraService = entraService;
    this._photoService = photoService;
    this._httpClient = httpClient;
    this._toastService = toastService;
    this.loadingText = assetsDataService.GetUiConfigData().loadingText;
    this.toastText = assetsDataService.GetUiConfigData().toastText;
  }

// Clears the email data
  public clearEmailData(): void {
    this.emailMessageData.next(new EmailPreviewResponse());
  }


  public createPlainTextEmailBody(htmlBody: string): string {
    // Extract the PO report URL using regex
    const urlRegex = /<a href='([^']*)'>[^<]*<\/a>/;
    const match = htmlBody.match(urlRegex);
    const poReportUrl = match ? match[1] : '';
    
    // Convert HTML to plain text
    let plainText = htmlBody
      .replace(/<head>[\s\S]*?<\/head>/gi, '') // Remove head section with styles
      .replace(/<style>[\s\S]*?<\/style>/gi, '') // Remove any style tags
      .replace(/<strong>(.*?)<\/strong>/g, '$1') // Remove strong tags but keep content
      .replace(/<a\s+href=['"](.*?)['"]>(.*?)<\/a>/gi, '$2: $1') // Format links as text: url
      .replace(/<[^>]+>/g, '') // Remove all remaining HTML tags
      .replace(/&nbsp;/g, ' ') // Replace HTML entities
      .replace(/\s{3,}/g, '\n') // Replace 3 or more spaces with newlines
      .trim(); // Trim leading/trailing whitespace
    
    // Clean up the text formatting
    plainText = plainText
      .split('\n')
      .filter(line => line.trim() !== '')
      .join('\n\n');
    
    return plainText;
  }

  /**
   * Generates an email preview for a receiving log entry.
   * The `emailMessageData` BehaviorSubject is updated with the preview content,
   * allowing components subscribed to it to automatically update with the generated preview.
   *
   * Method is called within the relevant components when the user requests an email preview.
   *
   * @param emailPreviewRequest The request containing the log data and email configuration.
   */
  public generateEmailPreview(emailPreviewRequest: EmailPreviewRequest): void {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.loadingEmailPreview });
    this.GetEmailPreview(emailPreviewRequest).subscribe({
      next: (response: EmailPreviewResponse) => {
        this.emailMessageData.next(response);
        console.log('emailMessageData within email service', this.emailMessageData.value);
     
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error generating email preview:', error);
        this._toastService.showWarningMessage(this.toastText.email.preview.error);
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }


  /**
  * Submits a new receiving log with optional photos. On success, the `fetchedLogs` BehaviorSubject will be updated 
  * if necessary, and components subscribed to it will automatically reflect the changes.
  * 
  * This method is called within the `LogFormComponent` when the user submits a new log.
  * 
  * @param receivingLogRow The receiving log data to submit.
  * @param photos Optional collection of photo data to upload with the log.
  * @param onSuccessFunction Optional callback function to execute after successful submission.
  */
  public sendEmail(
    receivingLogRequest: AddReceivingLogRequest,
    photos: LogEntryPhoto[],
    onSuccessFunction?: Function
  ): void {

      this.dataLoading.next({ 
        isLoading: true, 
        message:  photos.length> 0 && receivingLogRequest.emailData?.includePhotosInEmail ?this.loadingText.sendingEmailWithPhotos : this.loadingText.sendingEmailWithoutPhotos 
      });
          convertPhotosToFiles(photos)
          .then(photoFiles => {
          this.PostEmail(receivingLogRequest, photoFiles).subscribe({
            next: (response: OperationStatus) => {
              this._toastService.handleOperationStatus(response, 'Email');
              if (onSuccessFunction) {
                onSuccessFunction();
              }
            },
            error: () => {
              this.createErrorHandler()
              this.dataLoading.next(this.dataLoadingDefault);
            },
            complete: () => {
              this.dataLoading.next(this.dataLoadingDefault);
            }
          });
        })
        .catch(error => {
          console.error('Error converting photos to files:', error);
          this.dataLoading.next(this.dataLoadingDefault);
          this._toastService.showWarningMessage(this.toastText.photos.processing.error);
        });
      }

   
 
  
  private createErrorHandler() {
    return (err: HttpErrorResponse) => {
      console.error('Error sending email:', err);
      this.dataLoading.next(this.dataLoadingDefault);
      let errorMessage = this.toastText.email.send.error;
      if (err.status === 413) {
        errorMessage = this.toastText.photos.processing.sizeError;
      } else if (err.error?.message) {
        errorMessage = err.error.message;
      }
      this._toastService.showWarningMessage(errorMessage);
    };
  }



    
      /**
 * Sends an email with photos from Azure storage
 */
public sendEmailWithAzurePhotos(
  receivingLogRequest: AddReceivingLogRequest, 
  onSuccessFunction?: Function
): void {
const dataLoadingMessage = receivingLogRequest.emailData?.includePhotosInEmail ? this.loadingText.sendingEmailWithPhotos : this.loadingText.sendingEmailWithoutPhotos;
  this.dataLoading.next({ 
    isLoading: true, 
    message: dataLoadingMessage 
  });
      this.PostEmailWithAzurePhotos(receivingLogRequest).subscribe({
        next: (response: OperationStatus) => {
          this._toastService.handleOperationStatus(response, 'Email');
          if (onSuccessFunction) {
            onSuccessFunction();
          }
          this.dataLoading.next(this.dataLoadingDefault);
        },
        error: this.createErrorHandler(),
        complete: () => {
          this.dataLoading.next(this.dataLoadingDefault);
        }
      });
    }
  /** ############################################################################################################################## 
 * HTTP request functions that make API calls to the backend to perform operations such as sending an email.
 * These functions handle the actual HTTP requests to the backend API, 
 * and they acquire an access token from `EntraService` before making the request.
 * 
 * The following operations are handled by the HTTP request functions:
 * - Generate an email preview based on receiving log data.
 * - Submit a new receiving log with photos.
 */



  /**
   * HTTP Request to generate an email preview based on receiving log data.
   * Makes a POST request to fetch the email preview according to the specified request parameters.
   *
   * @param emailPreviewRequest The request containing log data and email details.
   * @returns An observable that resolves to the response containing the email preview content.
   */
  public GetEmailPreview(
    emailPreviewRequest: EmailPreviewRequest
  ): Observable<EmailPreviewResponse> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/emails/generate-email-preview`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient
          .post<EmailPreviewResponse>(url, emailPreviewRequest, {
            headers: headers,
          })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }



  
/**
 * Sends an email with optional photo attachments.
 * Makes a POST request to the email API endpoint with the receiving log data and optional photo files.
 * The request includes:
 * - The receiving log data as a JSON string
 * - Photo files (if provided) as multipart form data
 * 
 * @param receivingLogRequest The request containing the log data and email configuration
 * @param photoFiles Optional array of photo files to attach to the email
 * @returns An observable that resolves to the operation status of the email send
 */
public PostEmail(
  receivingLogRequest: AddReceivingLogRequest,
  photoFiles?: File[]
): Observable<OperationStatus> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
  
      const url = `${environment.apiUrl}/V1/emails/send-email`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      });
      
      // Create FormData object to hold both the log data and files
      const formData = new FormData();
      
      // Add the receiving log data as JSON string in a form field
      formData.append('ReceivingLog', JSON.stringify(receivingLogRequest));
      
      // Add each photo file to the form data
      if (photoFiles && photoFiles.length > 0) {
        for (let i = 0; i < photoFiles.length; i++) {
          formData.append('Photos', photoFiles[i], photoFiles[i].name);
        }
      }
      
  
      return this._httpClient
        .post<OperationStatus>(url, formData, {
          headers: headers
        })
        .pipe(
          catchError((error) => handleErrorFunction(error))
        );
    })
  );
}



 
/**
 * Sends an email using photos stored in Azure Blob Storage.
 * Makes a POST request to the email API endpoint with the receiving log data and blob names of photos.
 * The request includes:
 * - The receiving log data with blob names of photos to attach
 * - The photos are retrieved from Azure Blob Storage using the provided blob names
 * 
 * @param receivingLogRequest The request containing the log data, email configuration, and photo blob names
 * @returns An observable that resolves to the operation status of the email send
 */
public PostEmailWithAzurePhotos(
  receivingLogRequest: AddReceivingLogRequest,
): Observable<OperationStatus> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
  
      const url = `${environment.apiUrl}/V1/emails/send-email-with-blob-names`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      });
  
      return this._httpClient
        .post<OperationStatus>(url, receivingLogRequest, {
          headers: headers
        })
        .pipe(
          catchError((error) => handleErrorFunction(error))
        );
    })
  );
}
}